import React from 'react';
import PropTypes from 'prop-types';
import Row from 'react-bootstrap/lib/Row';
import Col from 'react-bootstrap/lib/Col';

import t from '../../lib/I18n';
import User from '../../records/User';
import PasswordChange from '../../records/customer/PasswordChange';
import CustomerActions from '../../actions/CustomerActions';
import ModalActions from '../../actions/ModalActions';
import NotificationActions from '../../actions/NotificationActions';
import UserStore from '../../stores/UserStore';
import {displayNotifications} from '../../helpers/NotificationCentre';

import BaseModal from '../general/BaseModal';
import FormattedHTMLMessage from '../general/FormattedHTMLMessage';
import {VisibleBAlert, bAlertType} from '../general/BAlert';
import Password from './Password';

export default function PromptPasswordChangeModal({user = UserStore.getCurrent()}) {
    const [error, setError] = React.useState();
    const [form, updateForm] = React.useState(new PasswordChange());
    const onChange = (field, value) => {
        updateForm(current => current.set(field, value));
        setError(null);
    };

    const [isCancelling, setCancelling] = React.useState(false);
    const onClose = () => {
        ModalActions.close();
        displayNotifications();
    };

    const [submitted, setSubmitted] = React.useState();
    const changePassword = React.useCallback(async () => {
        setSubmitted(true);
        if (!form.isComplete()) {
            return;
        }
        try {
            await CustomerActions.updateAuthenticatedProperties({
                user,
                currentPassword: form.currentPassword,
                changes: {
                    password: form.newPassword,
                },
            });
            NotificationActions.showNotification('login.promptPasswordChange.success');
            onClose();
        } catch (err) {
            setError(err);
        }
    }, [form, user]);

    const modalProps = {
        header: false,
        disableEscape: true,
        isAutofocusing: false,
        variant: 'light',
    };

    if (isCancelling) {
        return (
            <BaseModal
                {...modalProps}
                className="modal--sm"
                onClose={onClose}
                onContinue={() => setCancelling(!isCancelling)}
                continueText="login.promptPasswordChange.button.back"
            >
                <h2>
                    {t('login.promptPasswordChange.cancel.title', {firstNames: user.firstNames})}
                </h2>
                <FormattedHTMLMessage
                    message="login.promptPasswordChange.cancel.confirm"
                    tagName="p"
                    className="text-weight--bold padding--vert-sm"
                    splitParagraphs
                />
            </BaseModal>
        );
    }

    return (
        <BaseModal
            {...modalProps}
            onContinue={changePassword}
            onClose={() => setCancelling(!isCancelling)}
            continueText="login.promptPasswordChange.button.save"
        >
            <h2>{t('login.promptPasswordChange.title', {firstNames: user.firstNames})}</h2>
            <FormattedHTMLMessage
                message="login.promptPasswordChange.information"
                tagName="p"
                className="text-weight--bold padding--vert-sm"
            />
            <FormattedHTMLMessage message="login.promptPasswordChange.supplementary" tagName="p" />
            <FormattedHTMLMessage message="login.promptPasswordChange.defer" tagName="p" />
            <Row>
                <Col sm={6}>
                    <Password
                        field="currentPassword"
                        form={form}
                        onChange={onChange}
                        visited={submitted}
                        label={t('login.promptPasswordChange.currentPassword')}
                        showPasswordIcon
                    />
                </Col>
                <Col sm={6}>
                    <Password
                        id="newPassword"
                        field="newPassword"
                        form={form}
                        onChange={onChange}
                        visited={submitted}
                        label={t('login.promptPasswordChange.newPassword')}
                        autoComplete="new-password"
                        showPasswordHints
                        showPasswordIcon
                    />
                </Col>
            </Row>
            {error && (
                <VisibleBAlert type={bAlertType.DANGER}>
                    {t('login.promptPasswordChange.error')}
                </VisibleBAlert>
            )}
            {submitted && !form.isComplete() && (
                <VisibleBAlert type={bAlertType.DANGER}>
                    {t('login.promptPasswordChange.validationError')}
                </VisibleBAlert>
            )}
        </BaseModal>
    );
}

PromptPasswordChangeModal.propTypes = {
    user: PropTypes.instanceOf(User).isRequired,
};
